"use client";

import { useEffect, useRef, useState, useCallback } from "react";

interface UseStickyScrollOptions {
  /** Top offset when sticky at top (e.g., navbar height + gap) */
  topOffset?: number;
  /** Bottom offset when sticky at bottom */
  bottomOffset?: number;
  /** Whether the sticky behavior is enabled */
  enabled?: boolean;
}

interface StickyScrollStyle {
  position: "sticky" | "relative";
  top?: number;
  bottom?: number;
  alignSelf?: string;
}

/**
 * Hook for smart sticky scroll behavior.
 * The element scrolls naturally with the page but sticks at boundaries:
 * - When scrolling DOWN: sticks when bottom reaches viewport bottom
 * - When scrolling UP: sticks when top reaches the topOffset
 *
 * This creates a natural scrolling experience for sidebars that are
 * taller than the viewport, similar to Airbnb's booking card.
 */
export function useStickyScroll({
  topOffset = 144, // navbar (80px) + sticky nav (48px) + gap (16px)
  bottomOffset = 24,
  enabled = true,
}: UseStickyScrollOptions = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<StickyScrollStyle>({
    position: "sticky",
    top: topOffset,
  });

  const lastScrollY = useRef(0);
  const currentTop = useRef(topOffset);
  const isInitialized = useRef(false);

  const updatePosition = useCallback(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const viewportHeight = window.innerHeight;

    // If container is shorter than available viewport space, just use normal sticky
    const availableHeight = viewportHeight - topOffset - bottomOffset;
    if (containerHeight <= availableHeight) {
      if (style.position !== "sticky" || style.top !== topOffset) {
        setStyle({ position: "sticky", top: topOffset });
      }
      return;
    }

    const scrollY = window.scrollY;
    const scrollDelta = scrollY - lastScrollY.current;
    const isScrollingDown = scrollDelta > 0;

    // Calculate the bounds
    const minTop = viewportHeight - containerHeight - bottomOffset; // When bottom hits viewport bottom
    const maxTop = topOffset; // When top hits the topOffset

    if (!isInitialized.current) {
      // Initialize based on current scroll position
      currentTop.current = maxTop;
      isInitialized.current = true;
    }

    // Update the current top position based on scroll direction
    if (isScrollingDown) {
      // Scrolling down: move container up (decrease top)
      currentTop.current = Math.max(minTop, currentTop.current - scrollDelta);
    } else {
      // Scrolling up: move container down (increase top)
      currentTop.current = Math.min(maxTop, currentTop.current - scrollDelta);
    }

    // Clamp the value
    currentTop.current = Math.max(minTop, Math.min(maxTop, currentTop.current));

    lastScrollY.current = scrollY;

    // Only update state if the value actually changed
    const newTop = Math.round(currentTop.current);
    if (style.top !== newTop) {
      setStyle({
        position: "sticky",
        top: newTop,
        alignSelf: "flex-start",
      });
    }
  }, [enabled, topOffset, bottomOffset, style.position, style.top]);

  useEffect(() => {
    if (!enabled) return;

    // Initialize on mount
    lastScrollY.current = window.scrollY;
    updatePosition();

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        updatePosition();
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updatePosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updatePosition);
    };
  }, [enabled, updatePosition]);

  return {
    containerRef,
    style,
  };
}
